import * as React from "react"
import * as ReactDOM from "react-dom/client"
import { gettext, interpolate, qs, qsa } from "../utils/dom"

const TestimonialContext = React.createContext()
const useTestimonialContext = () => React.useContext(TestimonialContext)

const DEFAULTS = {
  search: true,
  paginateBy: 12,
  listBase: "",
  support: "%(name)s",
  no_testimonial_found: "",
}

export const initTestimonials = () => {
  let data
  for (const testimonialsEl of qsa("[data-testimonials]")) {
    if ((data = qs("script", testimonialsEl))) {
      const root = ReactDOM.createRoot(testimonialsEl)
      root.render(
        <TestimonialContext.Provider
          value={{ ...DEFAULTS, ...testimonialsEl.dataset }}
        >
          <TestimonialsApp data={JSON.parse(data.textContent || {})} />
        </TestimonialContext.Provider>,
      )
    }
  }
}

// Thanks, https://stackoverflow.com/a/37511463
const unaccent = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")

const filterTestimonials = (data, filterWord) => {
  const term = unaccent(filterWord).toLowerCase()
  return data.filter((obj) => obj.searchString.includes(term))
}

const TestimonialsApp = ({ data }) => {
  const testimonials = React.useMemo(
    () =>
      data.map((t) => ({ ...t, searchString: unaccent(t.name).toLowerCase() })),
    [data],
  )
  //console.log({ testimonials })
  const { paginateBy, search, no_testimonial_found } = useTestimonialContext()
  const [slices, setSlices] = React.useState(1)
  const [filterWord, setFilterWord] = React.useState("")
  const moreButtonRef = React.useRef("")

  const filteredTestimonials = filterTestimonials(testimonials, filterWord)
  const shownTestimonials = filteredTestimonials.slice(0, slices * paginateBy)

  const showNext = slices * paginateBy < filteredTestimonials.length
  // const showPrevious = (slices - 1) * paginateBy > 0

  React.useLayoutEffect(() => {
    window.dispatchEvent(new CustomEvent("masonry"))
  })

  const observer = new IntersectionObserver((entries, observer) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        setSlices(slices + 1)
        observer.unobserve(entry.target)
      }
    }
  })

  React.useEffect(() => {
    if (moreButtonRef?.current) {
      observer.observe(moreButtonRef.current)
    }
  })

  return (
    <>
      {search
        ? <div className="form plugin plugin--testimonials-search">
            <span className="testimonials__search">
              <input
                className="input__search"
                type="text"
                placeholder={gettext("suchen")}
                value={filterWord}
                onInput={(e) => setFilterWord(e.target.value)}
              />
            </span>
          </div>
        : null}
      {shownTestimonials.length < 1
        ? <div className="">
            {no_testimonial_found || gettext("Keine Botschaften gefunden.")}
          </div>
        : <div className="testimonials-grid masonry">
            {shownTestimonials.map((testimonial) => (
              <TestimonialBox key={testimonial.id} data={testimonial} />
            ))}
          </div>}
      {showNext ? <div ref={moreButtonRef} /> : null}
      {/* showNext || showPrevious
        ? <div className="testimonial__show-more">
          {showPrevious
            ? <button
              type="button"
              className="button button--more"
              onClick={() => {
                setSlices(slices - 1)
              }}
            >
              {gettext("previous page")}
            </button>
            : null}
          {showNext
            ? <button
              type="button"
              className="button button--more"
              onClick={() => {
                setSlices(slices + 1)
              }}
            >
              {gettext("next page")}
            </button>
            : null}
        </div>
        : null */}
    </>
  )
}

const TestimonialBox = ({ data }) => {
  const { listBase, support } = useTestimonialContext()
  const sharingText = `${encodeURI(interpolate(support, data, true))} \n`
  const sharingURL = `${listBase}${data.id}/`

  return (
    <div className="testimonial masonry__cell">
      <img
        className="testimonial__image"
        src={data.portrait}
        alt={`${gettext("Portrait of")} ${data.name}`}
      />
      <div className="testimonial__box accented">
        {data.statement
          ? <blockquote className="testimonial__statement">
              «{data.statement}»
            </blockquote>
          : null}
        {data.statement ? <div className="testimonial__separator" /> : null}
        <p className="testimonial__name">{data.name}</p>
        <p className="testimonial__profession">{data.profession}</p>
        {data.sharing_image
          ? <div className="testimonial__sharing">
              <ShareLink
                title={gettext("Share on Facebook")}
                url={`https://www.facebook.com/sharer.php?u=${sharingURL}?landscape`}
              >
                <svg className="icon">
                  <use xlinkHref="#icon-facebook" />
                </svg>
              </ShareLink>
              <ShareLink
                title={gettext("Share on Whatsapp")}
                url={`https://api.whatsapp.com/send?phone=&text=${sharingText}${sharingURL}?landscape`}
              >
                <svg className="icon">
                  <use xlinkHref="#icon-whatsapp" />
                </svg>
              </ShareLink>
              <ShareLink
                title={gettext("Share on Telegram")}
                url={`https://t.me/share/url?url=${sharingURL}&text=${sharingText}`}
              >
                <svg className="icon">
                  <use xlinkHref="#icon-telegram" />
                </svg>
              </ShareLink>
              <DownloadLink
                src={data.sharing_image}
                title={gettext("Share on Instagram")}
              >
                <svg className="icon">
                  <use xlinkHref="#icon-instagram" />
                </svg>
              </DownloadLink>
              <DownloadLink
                src={data.sharing_image}
                title={gettext("Download testimonial")}
              >
                <svg className="icon">
                  <use xlinkHref="#icon-download" />
                </svg>
              </DownloadLink>
            </div>
          : null}
      </div>
    </div>
  )
}

const ShareLink = ({ title, url, children }) => {
  return (
    <a
      className="some-link"
      href={url}
      title={title}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="sr-only">{title}</span>
      {children}
    </a>
  )
}

const DownloadLink = ({ title, src, children }) => {
  return (
    <a
      className="some-link"
      href={src}
      title={title}
      download="testimonial.png"
    >
      <span className="sr-only">{title}</span>
      {children}
    </a>
  )
}
